import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { login } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Ticket, AlertCircle } from 'lucide-react';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { loginUser } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await login(email, password);
            loginUser(response.data.token, response.data.user);
            
            // Redirect based on role
            if (response.data.user.ruolo === 'cliente') {
                navigate('/dashboard');
            } else {
                navigate('/operator');
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore durante il login');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2">
            {/* Left side - Form */}
            <div className="flex items-center justify-center p-8 bg-white">
                <div className="w-full max-w-md space-y-8 animate-fade-in">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-indigo-500 mb-6">
                            <Ticket className="w-8 h-8 text-white" />
                        </div>
                        <h1 className="text-3xl font-bold text-slate-900">Bentornato</h1>
                        <p className="mt-2 text-slate-600">Accedi al portale di assistenza</p>
                    </div>

                    <Card className="border-slate-200 shadow-sm">
                        <CardHeader className="space-y-1 pb-4">
                            <CardTitle className="text-xl">Accedi</CardTitle>
                            <CardDescription>Inserisci le tue credenziali per continuare</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {error && (
                                    <Alert variant="destructive" className="bg-red-50 border-red-200">
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription>{error}</AlertDescription>
                                    </Alert>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="email">Email</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="nome@esempio.com"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        data-testid="login-email-input"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label htmlFor="password">Password</Label>
                                        <Link 
                                            to="/forgot-password" 
                                            className="text-xs text-indigo-600 hover:text-indigo-500"
                                            data-testid="forgot-password-link"
                                        >
                                            Password dimenticata?
                                        </Link>
                                    </div>
                                    <Input
                                        id="password"
                                        type="password"
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        data-testid="login-password-input"
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full bg-slate-900 hover:bg-slate-800"
                                    disabled={loading}
                                    data-testid="login-submit-btn"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Accesso in corso...
                                        </>
                                    ) : (
                                        'Accedi'
                                    )}
                                </Button>
                            </form>

                            <div className="mt-6 text-center text-sm text-slate-600">
                                Non hai un account?{' '}
                                <Link to="/register" className="text-indigo-600 hover:text-indigo-500 font-medium">
                                    Registrati
                                </Link>
                            </div>
                        </CardContent>
                    </Card>

                    <p className="text-center text-xs text-slate-500">
                        Inserisci i dati di accesso
                    </p>
                </div>
            </div>

            {/* Right side - Image */}
            <div className="hidden lg:block relative">
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1764437358284-4721473936f8?crop=entropy&cs=srgb&fm=jpg&q=85)' }}
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/20 to-slate-900/40" />
                </div>
                <div className="absolute bottom-10 left-10 right-10">
                    <blockquote className="text-white">
                        <p className="text-xl font-medium leading-relaxed">
                            "Il nostro team è sempre pronto ad aiutarti con qualsiasi problema tu possa avere."
                        </p>
                        <footer className="mt-4 text-white/80">— Team Supporto</footer>
                    </blockquote>
                </div>
            </div>
        </div>
    );
}
